/* eslint-disable max-lines */
/* eslint-disable react/jsx-handler-names */
"use client";
import PropTypes from "prop-types";
import React, { useEffect, useLayoutEffect, useRef } from "react";
import { useSelector } from "react-redux";
import RecentSearch from "../mobileSearch/searchComponents/recentSearch";
import Feed from "../../../containers/dynamicHomePage/dynamicHomeComponent";
import DefaultSearchFeedSkeleton from "./DefaultSearchFeedSkeleton";
import { lockBody } from "../../../utils/document";
import { getLocalStorageItem } from "../../../utils/storage/localStorageUtil";
import { DEFAULT_SEARCH_HIDDEN_SCROLLBAR_CLASS, DESKTOP_DEFAULT_SEARCH_EMPTY_HEIGHT_PX } from "./constants";

/**
 * Default search empty state: recent searches plus feed widgets (`pageType=search`, `geoId` from catalog).
 *
 * @component
 * @param {Object} props - The component props
 * @param {Array<object>} props.widgets - Feed widgets from the search page feed API
 * @param {boolean} props.isMobile - Whether the device is mobile
 * @param {boolean} [props.isFeedLoading] - When true, shows loader inside a fixed-height desktop shell
 * @param {Function} [props.onDesktopPanelLayout] - Desktop: reports compact height for parent dropdown animation
 * @param {Function} [props.setIsSearchFocused] - Desktop: focus overlay when content exists
 * @param {Function} [props.onNavigate] - Close search on navigation
 * @param {Object} [props.onSearchTrackEvent] - Analytics handlers for recent search
 * @returns {JSX.Element} The rendered default search panel
 */
const DefaulSearchPage = ({
  widgets = [],
  isMobile,
  isFeedLoading = false,
  onDesktopPanelLayout,
  onSearchTrackEvent = {},
  setIsSearchFocused,
  onNavigate = () => {},
}) => {
  const isNavigating = useSelector((state) => state?.common?.isNavigating);
  const recentBlockRef = useRef(null);

  const fetchRecentSearchHistory = (() => {
    try {
      const data = getLocalStorageItem("recentSearchList");
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error("Error parsing recent search history:", error);
      return [];
    }
  })();
  const fetchRecentSearchList = Object.values(fetchRecentSearchHistory?.recentSearchList || []);
  const hasFeedWidgets = Array.isArray(widgets) && widgets.length > 0;
  const showFeedLoader = isFeedLoading && !hasFeedWidgets;
  const isDesktopExpanded = !isMobile && (showFeedLoader || hasFeedWidgets);
  const hasPanelContent = hasFeedWidgets || fetchRecentSearchList.length > 0 || showFeedLoader;

  useLayoutEffect(() => {
    if (isMobile || !onDesktopPanelLayout) {
      return;
    }

    const recentHeight = recentBlockRef.current?.offsetHeight ?? 0;
    const verticalPadding = 24;
    const compactHeightPx = Math.max(DESKTOP_DEFAULT_SEARCH_EMPTY_HEIGHT_PX, recentHeight + verticalPadding);

    onDesktopPanelLayout({
      isExpanded: isDesktopExpanded,
      compactHeightPx,
    });
  }, [fetchRecentSearchList.length, isDesktopExpanded, isMobile, onDesktopPanelLayout]);

  useEffect(() => {
    if (!isMobile && !showFeedLoader && hasPanelContent) {
      setIsSearchFocused?.(true);
      lockBody();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFeedLoader]);

  useEffect(() => {
    if (isNavigating) {
      onNavigate();
    }
  }, [isNavigating, onNavigate]);

  const recentSearchBlock =
    fetchRecentSearchList?.length > 0 ? (
      <div ref={recentBlockRef} className="px-16 pb-12">
        <RecentSearch
          recentSearch={fetchRecentSearchList}
          onSearchTrackEvent={onSearchTrackEvent}
          isFromSearch={true}
          onClose={onNavigate}
        />
      </div>
    ) : null;

  if (isMobile) {
    return (
      <div
        className={`flex h-full w-full flex-col overflow-y-auto bg-white-900 ${DEFAULT_SEARCH_HIDDEN_SCROLLBAR_CLASS} ${hasPanelContent ? "py-12" : ""}`}
        id="defaultSearchPage"
      >
        {recentSearchBlock}
        {showFeedLoader && <DefaultSearchFeedSkeleton isMobile={true} />}
        {hasFeedWidgets && <Feed widgets={widgets} isMobile={isMobile} isCampaignLoading={false} />}
      </div>
    );
  }

  return (
    <div id="defaultSearchPage" className="flex w-full flex-col pt-12">
      {recentSearchBlock}
      {showFeedLoader && <DefaultSearchFeedSkeleton isMobile={false} />}
      {hasFeedWidgets && <Feed widgets={widgets} isMobile={false} isCampaignLoading={false} />}
    </div>
  );
};
export default React.memo(DefaulSearchPage);

DefaulSearchPage.propTypes = {
  widgets: PropTypes.arrayOf(PropTypes.object),
  isMobile: PropTypes.bool.isRequired,
  isFeedLoading: PropTypes.bool,
  onDesktopPanelLayout: PropTypes.func,
  onSearchTrackEvent: PropTypes.object,
  setIsSearchFocused: PropTypes.func,
  onNavigate: PropTypes.func,
};
